import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/ui/toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const { addToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, { email });
      setSubmitted(true);
      addToast({
        title: "Email sent",
        description: "Check your email for password reset instructions.",
        variant: "success"
      });
    } catch (error: any) {
      addToast({
        title: "Request failed",
        description: error.response?.data?.message || 'Failed to send reset email. Please try again.',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-4 fade-in">
          <img 
            src="/logo.svg" 
            alt="LiveKit Logo" 
            className="mx-auto h-24 w-24"
          />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reset password</h1>
            <p className="text-muted-foreground mt-2">
              {submitted ? 'Check your email' : 'Enter your email to reset your password'}
            </p>
          </div>
        </div>

        <Card className="slide-in shadow-2xl border-0 bg-card/50 backdrop-blur-sm">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl text-center font-semibold">Forgot password</CardTitle>
                <CardDescription className="text-center">
                  We'll send you a link to reset your password
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  icon={<Mail className="h-4 w-4" />}
                  className="h-12"
                />
              </CardContent>
              
              <CardFooter className="flex flex-col space-y-4 pt-6">
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base"
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
                
                <Link 
                  to="/login" 
                  className="flex items-center justify-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to login
                </Link>
              </CardFooter>
            </form>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl text-center font-semibold">Check your email</CardTitle>
                <CardDescription className="text-center">
                  We've sent password reset instructions to {email}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="bg-muted p-4 rounded-lg text-sm text-muted-foreground">
                  <p>Didn't receive the email? Check your spam folder or try again.</p>
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col space-y-4 pt-6">
                <Button 
                  onClick={() => setSubmitted(false)}
                  className="w-full h-12 text-base"
                  variant="outline"
                >
                  Try another email
                </Button>
                
                <Link 
                  to="/login" 
                  className="flex items-center justify-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to login
                </Link>
              </CardFooter>
            </>
          )}
        </Card>
        
        <div className="text-center text-xs text-muted-foreground fade-in">
          <p>© 2024 LiveKit. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
